import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Typography,
    Grid,
    Chip,
    Divider,
    makeStyles
} from "@material-ui/core";
import {
    Close as CloseIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    Language as LanguageIcon,
    Devices as DevicesIcon,
    Computer as ComputerIcon,
    Smartphone as SmartphoneIcon,
    Tablet as TabletIcon,
    LocationOn as LocationIcon,
    TrendingUp as CampaignIcon,
    AccessTime as AccessTimeIcon,
    Public as PublicIcon
} from "@material-ui/icons";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

const useStyles = makeStyles((theme) => ({
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        paddingBottom: theme.spacing(1)
    },
    section: {
        marginBottom: theme.spacing(3)
    },
    sectionTitle: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
        color: theme.palette.primary.main,
        fontWeight: 600
    },
    infoRow: {
        display: "flex",
        alignItems: "center",
        marginBottom: theme.spacing(1.5),
        padding: theme.spacing(1),
        backgroundColor: theme.palette.background.default,
        borderRadius: 8
    },
    label: {
        fontWeight: 600,
        minWidth: 140,
        color: theme.palette.text.secondary,
        fontSize: "0.875rem"
    },
    value: {
        color: theme.palette.text.primary,
        fontSize: "0.875rem"
    },
    chip: {
        marginRight: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5)
    },
    deviceIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.primary.main
    },
    emptyValue: {
        color: theme.palette.text.disabled,
        fontStyle: "italic"
    }
}));

const LeadDetailsModal = ({ open, onClose, lead }) => {
    const classes = useStyles();

    if (!lead) return null;

    const formatDate = (date) => {
        if (!date) return "-";
        try {
            return format(new Date(date), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
        } catch {
            return "-";
        }
    };

    const getDeviceIcon = (deviceType) => {
        switch (deviceType?.toLowerCase()) {
            case "mobile":
                return <SmartphoneIcon className={classes.deviceIcon} />;
            case "tablet":
                return <TabletIcon className={classes.deviceIcon} />;
            case "desktop":
                return <ComputerIcon className={classes.deviceIcon} />;
            default:
                return <DevicesIcon className={classes.deviceIcon} />;
        }
    };

    const getSourceColor = (source) => {
        const colors = {
            google: "#4285F4",
            facebook: "#1877F2",
            instagram: "#E4405F",
            direct: "#757575"
        };
        return colors[source?.toLowerCase()] || "#757575";
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box className={classes.header}>
                    <Typography variant="h6">
                        Detalhes do Lead
                    </Typography>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {/* Informações Pessoais */}
                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                        <PersonIcon fontSize="small" />
                        Informações Pessoais
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Box className={classes.infoRow}>
                                <Typography className={classes.label}>Nome:</Typography>
                                <Typography className={classes.value}>{lead.name || "-"}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box className={classes.infoRow}>
                                <Typography className={classes.label}>Telefone:</Typography>
                                <Typography className={classes.value}>{lead.phone || "-"}</Typography>
                            </Box>
                        </Grid>
                        {lead.email && (
                            <Grid item xs={12}>
                                <Box className={classes.infoRow}>
                                    <Typography className={classes.label}>E-mail:</Typography>
                                    <Typography className={classes.value}>{lead.email}</Typography>
                                </Box>
                            </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                            <Box className={classes.infoRow}>
                                <Typography className={classes.label}>Data de Cadastro:</Typography>
                                <Typography className={classes.value}>{formatDate(lead.createdAt)}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box className={classes.infoRow}>
                                <Typography className={classes.label}>Status:</Typography>
                                <Chip
                                    label={lead.status || "new"}
                                    size="small"
                                    color="primary"
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                <Divider />

                {/* Origem da Campanha */}
                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                        <CampaignIcon fontSize="small" />
                        Origem da Campanha
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Box className={classes.infoRow}>
                                <Typography className={classes.label}>Fonte:</Typography>
                                <Chip
                                    label={lead.utm_source || lead.sourceType || "Direto"}
                                    size="small"
                                    style={{
                                        backgroundColor: `${getSourceColor(lead.utm_source)}20`,
                                        color: getSourceColor(lead.utm_source),
                                        fontWeight: 600
                                    }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box className={classes.infoRow}>
                                <Typography className={classes.label}>Meio:</Typography>
                                <Typography className={classes.value}>
                                    {lead.utm_medium || <span className={classes.emptyValue}>Não informado</span>}
                                </Typography>
                            </Box>
                        </Grid>
                        {lead.utm_campaign && (
                            <Grid item xs={12}>
                                <Box className={classes.infoRow}>
                                    <Typography className={classes.label}>Campanha:</Typography>
                                    <Typography className={classes.value}>{lead.utm_campaign}</Typography>
                                </Box>
                            </Grid>
                        )}
                        {lead.utm_term && (
                            <Grid item xs={12} sm={6}>
                                <Box className={classes.infoRow}>
                                    <Typography className={classes.label}>Termo de Busca:</Typography>
                                    <Typography className={classes.value}>{lead.utm_term}</Typography>
                                </Box>
                            </Grid>
                        )}
                        {lead.utm_content && (
                            <Grid item xs={12} sm={6}>
                                <Box className={classes.infoRow}>
                                    <Typography className={classes.label}>Conteúdo:</Typography>
                                    <Typography className={classes.value}>{lead.utm_content}</Typography>
                                </Box>
                            </Grid>
                        )}
                        {lead.referrer && (
                            <Grid item xs={12}>
                                <Box className={classes.infoRow}>
                                    <Typography className={classes.label}>Referência:</Typography>
                                    <Typography className={classes.value} style={{ fontSize: "0.75rem" }}>
                                        {lead.referrer}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Box>

                <Divider />

                {/* Dispositivo e Navegador */}
                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                        <DevicesIcon fontSize="small" />
                        Dispositivo e Navegador
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Box className={classes.infoRow}>
                                <Typography className={classes.label}>Tipo:</Typography>
                                <Box display="flex" alignItems="center">
                                    {getDeviceIcon(lead.device_type)}
                                    <Typography className={classes.value}>
                                        {lead.device_type || <span className={classes.emptyValue}>Não identificado</span>}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        {lead.device_brand && (
                            <Grid item xs={12} sm={6}>
                                <Box className={classes.infoRow}>
                                    <Typography className={classes.label}>Marca:</Typography>
                                    <Typography className={classes.value}>{lead.device_brand}</Typography>
                                </Box>
                            </Grid>
                        )}
                        {lead.os_name && (
                            <Grid item xs={12} sm={6}>
                                <Box className={classes.infoRow}>
                                    <Typography className={classes.label}>Sistema Operacional:</Typography>
                                    <Typography className={classes.value}>
                                        {lead.os_name} {lead.os_version && `(${lead.os_version})`}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                        {lead.browser_name && (
                            <Grid item xs={12} sm={6}>
                                <Box className={classes.infoRow}>
                                    <Typography className={classes.label}>Navegador:</Typography>
                                    <Typography className={classes.value}>
                                        {lead.browser_name} {lead.browser_version && `(${lead.browser_version})`}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Box>

                <Divider />

                {/* Localização */}
                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                        <LocationIcon fontSize="small" />
                        Localização
                    </Typography>
                    <Grid container spacing={2}>
                        {lead.city && (
                            <Grid item xs={12} sm={6}>
                                <Box className={classes.infoRow}>
                                    <Typography className={classes.label}>Cidade:</Typography>
                                    <Typography className={classes.value}>{lead.city}</Typography>
                                </Box>
                            </Grid>
                        )}
                        {lead.region && (
                            <Grid item xs={12} sm={6}>
                                <Box className={classes.infoRow}>
                                    <Typography className={classes.label}>Estado/Região:</Typography>
                                    <Typography className={classes.value}>{lead.region}</Typography>
                                </Box>
                            </Grid>
                        )}
                        {lead.country && (
                            <Grid item xs={12} sm={6}>
                                <Box className={classes.infoRow}>
                                    <Typography className={classes.label}>País:</Typography>
                                    <Typography className={classes.value}>{lead.country}</Typography>
                                </Box>
                            </Grid>
                        )}
                        {lead.ipAddress && (
                            <Grid item xs={12} sm={6}>
                                <Box className={classes.infoRow}>
                                    <Typography className={classes.label}>IP:</Typography>
                                    <Typography className={classes.value} style={{ fontFamily: "monospace" }}>
                                        {lead.ipAddress}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Box>

                {/* Respostas do Quiz */}
                {(() => {
                    let answers = lead.quizAnswers;
                    if (typeof answers === "string") {
                        try {
                            answers = JSON.parse(answers);
                        } catch (e) {
                            return null;
                        }
                    }

                    if (!answers || Object.keys(answers).length === 0) return null;

                    return (
                        <>
                            <Divider />
                            <Box className={classes.section}>
                                <Typography className={classes.sectionTitle}>
                                    <EmailIcon fontSize="small" />
                                    Respostas do Formulário
                                </Typography>
                                <Grid container spacing={2}>
                                    {Object.entries(answers).map(([questionId, answerData]) => {
                                        // Skip internal fields
                                        if (['initialMessage', 'trackSource', 'trackId'].includes(questionId)) {
                                            return null;
                                        }

                                        // Se answerData é um objeto com question e answer
                                        const question = answerData?.question || questionId.replace(/^q\d+_?/, '').replace(/_/g, ' ');
                                        const answer = answerData?.answer || (typeof answerData === 'string' ? answerData : JSON.stringify(answerData));

                                        return (
                                            <Grid item xs={12} key={questionId}>
                                                <Box className={classes.infoRow}>
                                                    <Typography className={classes.label}>
                                                        {question}:
                                                    </Typography>
                                                    <Typography className={classes.value}>
                                                        {answer || <span className={classes.emptyValue}>Não respondido</span>}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Box>
                        </>
                    );
                })()}
            </DialogContent>
        </Dialog>
    );
};

export default LeadDetailsModal;
